import { formatPrice } from 'Utilities/formatPrice';
import { safeLocalStorage } from 'Utilities/storages';

interface ValuationBand {
  lowerBoundPercentage: number;
  upperBoundPercentage: number;
  upperPriceLimit: number;
}

export interface PriceRange {
  priceRangeHigh: string;
  priceRangeHighAbbr: string;
  priceRangeLow: string;
  priceRangeLowAbbr: string;
  priceRangeMidAbbr: string;
}

type PriceRangeBucket = 'variant' | 'control';

const ROUNDING_INCREMENT = 25;

const VALUATION_BANDS: ValuationBand[] = [
  { lowerBoundPercentage: 42.70, upperBoundPercentage: 4.00, upperPriceLimit: 2500 },
  { lowerBoundPercentage: 23.30, upperBoundPercentage: 3.50, upperPriceLimit: 5000 },
  { lowerBoundPercentage: 15.10, upperBoundPercentage: 3.00, upperPriceLimit: 10000 },
  { lowerBoundPercentage: 12.30, upperBoundPercentage: 2.50, upperPriceLimit: 15000 },
  { lowerBoundPercentage: 11.00, upperBoundPercentage: 2.25, upperPriceLimit: 20000 },
  { lowerBoundPercentage: 9.50, upperBoundPercentage: 2.00, upperPriceLimit: 25000 },
  { lowerBoundPercentage: 8.10, upperBoundPercentage: 1.75, upperPriceLimit: 40000 },
  { lowerBoundPercentage: 5.80, upperBoundPercentage: 1.50, upperPriceLimit: Infinity },
];

const getValuationBand = (price: number): ValuationBand => (
  VALUATION_BANDS.find(({ upperPriceLimit }) => price <= upperPriceLimit) ?? VALUATION_BANDS[VALUATION_BANDS.length - 1]
);

const roundToNearest = (value: number, nearest: number): number => Math.round(value / nearest) * nearest;

const abbreviatePrice = (value: number): string => {
  if (value < 1000) {
    return formatPrice(value);
  }

  const thousands = Math.round((value / 100)) / 10;

  return `£${thousands}k`;
};

const getStorageKey = (enquiryId: number) => `hasBeenBucketedForPriceRange${enquiryId}`;

export const getPriceRangeBucket = (enquiryId?: number): PriceRangeBucket | null => {
  if (!enquiryId) {
    return null;
  }

  const value = safeLocalStorage.getItem(getStorageKey(enquiryId));

  if (value === 'variant' || value === 'control') {
    return value;
  }

  return null;
};

export const setPriceRangeBucket = (enquiryId: number, bucket: PriceRangeBucket): void => {
  safeLocalStorage.setItem(getStorageKey(enquiryId), bucket);
};

export const formatRange = (range: PriceRange) => `${range.priceRangeLow} - ${range.priceRangeHigh}`;

export const getValueGroup = (price: number): string => {
  if (!Number.isFinite(price) || price <= 0) {
    return 'unknown';
  }

  const band = VALUATION_BANDS.find(({ upperPriceLimit }) => price <= upperPriceLimit);

  if (!band) {
    return 'unknown';
  }

  const bandIndex = VALUATION_BANDS.indexOf(band);
  const lowerLimit = bandIndex === 0 ? 0 : VALUATION_BANDS[bandIndex - 1].upperPriceLimit + 1;

  if (band.upperPriceLimit === Infinity) {
    return `${lowerLimit}+`;
  }

  return `${lowerLimit}-${band.upperPriceLimit}`;
};

export const getPriceRange = (price: number): PriceRange => {
  if (!Number.isFinite(price) || price <= 0) {
    throw new RangeError(`getPriceRange: price must be a positive finite number, got ${price}`);
  }

  const { lowerBoundPercentage, upperBoundPercentage } = getValuationBand(price);

  const low = roundToNearest(price * (1 - (lowerBoundPercentage / 100)), ROUNDING_INCREMENT);
  const high = roundToNearest(price * (1 + (upperBoundPercentage / 100)), ROUNDING_INCREMENT);

  return {
    priceRangeHigh: formatPrice(high),
    priceRangeHighAbbr: abbreviatePrice(high),
    priceRangeLow: formatPrice(low),
    priceRangeLowAbbr: abbreviatePrice(low),
    priceRangeMidAbbr: abbreviatePrice(price),
  };
};

export const getDisplayPrice = (price: number, enquiryId?: number): string | null => {
  if (!Number.isFinite(price) || price <= 0) {
    return null;
  }

  const bucket = getPriceRangeBucket(enquiryId);

  if (bucket === 'variant') {
    const range = getPriceRange(price);
    return formatRange(range);
  }

  return null;
};

export const shouldSuppressPriceRange = ({
  closingBidId,
  closingBidValue,
  displayPrice,
  postSaleAmount,
  sellerWillAcceptPrice,
}: {
  closingBidId?: number | null;
  closingBidValue?: number | null;
  displayPrice?: number | null;
  postSaleAmount?: number | null;
  sellerWillAcceptPrice?: number | null;
}): boolean => !!postSaleAmount || !!closingBidValue || !!displayPrice || !!sellerWillAcceptPrice || !!closingBidId;
